import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PeriodNav from '../components/PeriodSummary/PeriodNav';
import PeriodHero from '../components/PeriodSummary/PeriodHero';
import PeriodStats from '../components/PeriodSummary/PeriodStats';
import PeriodGraphs from '../components/PeriodSummary/PeriodGraphs';
import PeriodSuggestions from '../components/PeriodSummary/PeriodSuggestions';
import PeriodTimeline from '../components/PeriodSummary/PeriodTimeline';
import NewsFeed from '../components/NewsFeed';
import '../styles/PeriodSummaryPage.css';

// Dedicated weekly/monthly summary report page (linked to from NewsFeedTile's "View Summary"
// button). Fetches the single backing endpoint (GET /api/period-summary/:updateId) and renders,
// top to bottom: prev/next nav, hero (poster+description), highlighted stats, graphs,
// highlighted suggestions, per-day timeline, and finally a newsfeed scoped to just this period.
const PeriodSummaryPage = ({ apiBaseUrl }) => {
  const { updateId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`${apiBaseUrl}/period-summary/${updateId}`, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(json => {
        if (!cancelled) setData(json);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, updateId]);

  // The route param changes when using prev/next nav but the page component itself doesn't
  // remount, so without this the scroll position from the previous period would carry over.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [updateId]);

  if (loading) {
    return (
      <div className="period-summary-page">
        <p className="period-summary-loading">Loading summary...</p>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="period-summary-page">
        <h1>Summary not found</h1>
        <button className="btn" onClick={() => navigate('/')}>Back Home</button>
      </div>
    );
  }

  const { update, period, navigation, highlight, stats, top_media, timeline, suggestions } = data;
  const bgImage = update.image || update.poster_image || highlight?.image;
  
  // Build Activity Feed filters: date range + explicit status + exclude this summary itself
  const feedFilters = {
    start_date: period.start,
    end_date: period.end,
    exclude_update_id: update.id,
    ...(period.is_explicit && { explicit_only: true }),
    ...(!period.is_explicit && { explicit_only: false }),
  };

  return (
    <div className="period-summary-page">
      {bgImage && (
        <div className="period-summary-blurred-bg" style={{ backgroundImage: `url(${bgImage})` }} />
      )}
      <div className="period-summary-content-wrapper">
        <PeriodNav prev={navigation.prev} next={navigation.next} />
        <PeriodHero update={update} period={period} highlight={highlight} />
        <PeriodStats stats={stats} topMedia={top_media} timeline={timeline} period={period} />
        <PeriodGraphs stats={stats} timeline={timeline} />
        <PeriodSuggestions suggestions={suggestions} period={period} />
        <PeriodTimeline timeline={timeline} />
        <section className="period-summary-newsfeed">
          <h2>Activity Feed</h2>
          <NewsFeed
            apiBaseUrl={apiBaseUrl}
            filters={feedFilters}
            limit={15}
          />
        </section>
      </div>
    </div>
  );
};

export default PeriodSummaryPage;
